import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DashboardCalendar from '../components/DashboardCalendar';
import WeatherCard from '../components/WeatherCard';
import { ROUTE_PATHS } from '../config/navigation';
import {
  getSelfServiceContentFallback,
  hydrateSelfServiceContent
} from '../data/selfServiceContent';
import { EXTERNAL_APP_LINKS } from '../config/organization';
import { hydrateDocumentColumns, sortFeaturedFirst } from '../data/dashboardContent';

const fallbackAnnouncements = [
  {
    title: 'İş Sağlığı ve Güvenliği duyurusu',
    content: 'Güncellenen İSG prosedürleri doküman merkezinde yayımlandı.',
    createdAt: new Date().toISOString(),
    author: { firstName: 'Koloğlu', lastName: 'Holding' }
  },
  {
    title: 'Yıl sonu etkinlik takvimi paylaşımı',
    content: 'Yıl sonu organizasyon planı ve katılım detayları etkinlik takviminde yayımlandı.',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    author: { firstName: 'Kurumsal', lastName: 'İletişim' }
  }
];

const getCurrentLunchMenu = (menuItems) => {
  if (!menuItems.length) {
    return null;
  }

  const weekdayIndex = new Date().getDay();
  const weekdayMap = {
    1: 'Pazartesi',
    2: 'Salı',
    3: 'Çarşamba',
    4: 'Perşembe',
    5: 'Cuma'
  };

  const todayMenu = menuItems.find((item) => item.day === weekdayMap[weekdayIndex]);
  return todayMenu || menuItems[0];
};

const formatFeedDate = (value) => {
  if (!value) {
    return '';
  }
  try {
    return new Date(value).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long'
    });
  } catch (error) {
    return '';
  }
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selfServiceContent, setSelfServiceContent] = useState(() => getSelfServiceContentFallback());
  const [announcements, setAnnouncements] = useState([]);
  const [documentColumns, setDocumentColumns] = useState(() => hydrateDocumentColumns());
  const [departments, setDepartments] = useState([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryDepartment, setDirectoryDepartment] = useState('');

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'Hoş Geldiniz';

  const todayLabel = useMemo(() => {
    const now = new Date();
    return {
      date: now.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }),
      weekday: now.toLocaleDateString('tr-TR', { weekday: 'long' })
    };
  }, []);

  const activeCompanyId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      if (user.role === 'super_admin') {
        return localStorage.getItem('activeCompanyId') || '';
      }
      return user.companyId || '';
    } catch (error) {
      return '';
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [countData, managedContent, departmentData] = await Promise.all([
          api.getCounts(),
          api.getManagedContent(),
          api.getDepartments().catch(() => ({ departments: [] }))
        ]);

        setSelfServiceContent(
          managedContent?.success
            ? hydrateSelfServiceContent(managedContent.data?.selfService || {})
            : getSelfServiceContentFallback()
        );

        setDocumentColumns(
          hydrateDocumentColumns(managedContent?.data?.dashboard?.documentColumns)
        );

        setAnnouncements(
          countData.data?.latestAnnouncements?.length > 0
            ? countData.data.latestAnnouncements
            : fallbackAnnouncements
        );

        setDepartments(departmentData?.departments || []);
      } catch (error) {
        console.error('Dashboard verileri yüklenirken hata:', error);
        setSelfServiceContent(getSelfServiceContentFallback());
        setDocumentColumns(hydrateDocumentColumns());
        setAnnouncements(fallbackAnnouncements);
        setDepartments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [activeCompanyId]);

  const currentLunchMenu = getCurrentLunchMenu(selfServiceContent.lunchMenu || []);

  const handleDirectorySearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (directorySearch.trim()) {
      params.set('arama', directorySearch.trim());
    }
    if (directoryDepartment) {
      params.set('departman', directoryDepartment);
    }
    const query = params.toString();
    navigate(`${ROUTE_PATHS.directory}${query ? `?${query}` : ''}`);
  };

  const handleDirectoryClear = () => {
    setDirectorySearch('');
    setDirectoryDepartment('');
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kolin-dash">
      {/* --- Üst sıra: Hero | Uygulamalar | Hızlı Erişim --- */}
      <section className="kolin-card kolin-col-hero kolin-hero">
        <div className="kolin-hero-text">
          <p className="kolin-hero-eyebrow">Hoş Geldiniz,</p>
          <h1 className="kolin-hero-name">{fullName}</h1>
          <p className="kolin-hero-date">
            {todayLabel.date}
            <br />
            <span>{todayLabel.weekday}</span>
          </p>
        </div>
        <div className="kolin-hero-art" aria-hidden="true">
          <svg viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" strokeWidth="3">
              <line x1="110" y1="90" x2="40" y2="40" stroke="#9ec5fe" />
              <line x1="110" y1="90" x2="180" y2="45" stroke="#f7b267" />
              <line x1="110" y1="90" x2="45" y2="140" stroke="#83c5be" />
              <line x1="110" y1="90" x2="180" y2="135" stroke="#e07a5f" />
              <line x1="110" y1="90" x2="110" y2="20" stroke="#b8c0ff" />
            </g>
            <circle cx="110" cy="90" r="34" fill="#e7efff" stroke="#3b82f6" strokeWidth="3" />
            <circle cx="40" cy="40" r="11" fill="#3b82f6" />
            <circle cx="180" cy="45" r="11" fill="#f59e0b" />
            <circle cx="45" cy="140" r="11" fill="#14b8a6" />
            <circle cx="180" cy="135" r="11" fill="#ef4444" />
            <circle cx="110" cy="20" r="9" fill="#8b5cf6" />
            <path
              d="M100 84 l8 10 l16 -18"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </section>

      <section className="kolin-col-apps kolin-apps">
        {EXTERNAL_APP_LINKS.map((app) => (
          <a
            key={app.id}
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="kolin-app-card"
            aria-label={app.label}
          >
            <img src={app.imagePath} alt={app.label} className="kolin-app-card-img" />
          </a>
        ))}
      </section>

      {/* Hava durumu üst sıraya alındı (Rehber ile yer değiştirdi) */}
      <WeatherCard />

      {/* --- Orta sıra: Takvim | Koloğlu Rehber | Duyuru ve Haberler --- */}
      <section className="kolin-card kolin-col-takvim">
        <div className="kolin-card-head">
          <div className="kolin-card-title">
            <span className="kolin-head-icon"><i className="bx bxs-calendar-event" aria-hidden="true"></i></span>
            <h4>Takvim</h4>
          </div>
          <button
            type="button"
            className="kolin-card-link"
            onClick={() => navigate(ROUTE_PATHS.events)}
            aria-label="Etkinlik takvimini aç"
          >
            <i className="bx bx-right-arrow-alt"></i>
          </button>
        </div>
        <DashboardCalendar />
      </section>

      {/* Koloğlu Rehber — Hava durumu ile yer değiştirip orta sıraya alındı */}
      <section className="kolin-card kolin-col-rehber">
        <div className="kolin-card-head">
          <div className="kolin-card-title">
            <span className="kolin-head-icon"><i className="bx bxs-contact" aria-hidden="true"></i></span>
            <h4>Koloğlu Rehber</h4>
          </div>
        </div>
        <p className="kolin-rehber-hint">
          Koloğlu Holding çalışanlarıyla ilgili bilgilerin yer aldığı rehbere buradan ulaşabilirsiniz.
        </p>
        <form className="kolin-rehber-form" onSubmit={handleDirectorySearch}>
          <div className="kolin-rehber-search">
            <i className="bx bx-search" aria-hidden="true"></i>
            <input
              type="text"
              value={directorySearch}
              onChange={(event) => setDirectorySearch(event.target.value)}
              placeholder="İsim, unvan, departman, lokasyon veya dahili numara"
            />
          </div>
          <div className="kolin-rehber-row">
            <label className="kolin-rehber-field">
              <span>Departman</span>
              <select
                value={directoryDepartment}
                onChange={(event) => setDirectoryDepartment(event.target.value)}
              >
                <option value="">Tüm departmanlar</option>
                {departments.map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
              </select>
            </label>
            <div className="kolin-rehber-actions">
              <button type="submit" className="kolin-btn kolin-btn-primary">
                Ara
              </button>
              <button type="button" className="kolin-btn kolin-btn-ghost" onClick={handleDirectoryClear}>
                Filtreyi temizle
              </button>
            </div>
          </div>
        </form>
      </section>

      <section className="kolin-card kolin-col-duyuru">
        <div className="kolin-card-head">
          <div className="kolin-card-title">
            <span className="kolin-head-icon"><i className="bx bxs-megaphone" aria-hidden="true"></i></span>
            <h4>Duyuru ve Haberler</h4>
          </div>
          <button
            type="button"
            className="kolin-card-link"
            onClick={() => navigate(ROUTE_PATHS.announcements)}
            aria-label="Tüm duyuruları aç"
          >
            <i className="bx bx-right-arrow-alt"></i>
          </button>
        </div>
        {announcements.length > 0 ? (
          <ul className="kolin-feed">
            {announcements.slice(0, 6).map((item, index) => (
              <li key={item._id || index} className="kolin-feed-item">
                <span className="kolin-feed-dot" aria-hidden="true"></span>
                <div className="kolin-feed-body">
                  <p className="kolin-feed-title">{item.title}</p>
                  <p className="kolin-feed-meta">
                    {formatFeedDate(item.createdAt || item.publishDate)}
                    {item.author?.firstName
                      ? ` · ${item.author.firstName} ${item.author.lastName || ''}`.trimEnd()
                      : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="kolin-empty">Henüz duyuru bulunmuyor.</p>
        )}
      </section>

      {/* --- Alt sıra: Dokümanlar | Yemekhane menüsü --- */}
      <section className="kolin-card kolin-col-docs">
        <div className="kolin-card-head">
          <div className="kolin-card-title">
            <span className="kolin-head-icon"><i className="bx bxs-folder-open" aria-hidden="true"></i></span>
            <h4>Form, Prosedür ve Dokümanlar</h4>
          </div>
        </div>
        <div className="kolin-docs-grid">
          {documentColumns.map((column) => (
            <div key={column.key} className="kolin-docs-col">
              <button
                type="button"
                className="kolin-docs-head"
                onClick={() => navigate(column.path)}
              >
                <i className={column.icon} aria-hidden="true"></i>
                <span>{column.title}</span>
              </button>
              <ul className="kolin-docs-list">
                {sortFeaturedFirst(column.links).map((link) => (
                  <li key={link.label} className={link.featured ? 'is-featured' : ''}>
                    <button type="button" onClick={() => navigate(link.path)}>
                      <i className="bx bx-chevron-right" aria-hidden="true"></i>
                      {link.label}
                      {link.featured && (
                        <span className="kolin-docs-badge">
                          <i className="bx bxs-star" aria-hidden="true"></i>
                          Öne çıkan
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="kolin-card kolin-col-yemek">
        <div className="kolin-card-head">
          <div className="kolin-card-title">
            <span className="kolin-head-icon"><i className="bx bxs-dish" aria-hidden="true"></i></span>
            <h4>Yemekhane menüsü</h4>
          </div>
          <button
            type="button"
            className="kolin-card-link"
            onClick={() => navigate(`${ROUTE_PATHS.selfService}#yemekhane`)}
            aria-label="Yemekhane menüsünü aç"
          >
            <i className="bx bx-right-arrow-alt"></i>
          </button>
        </div>
        {currentLunchMenu ? (
          <div className="kolin-menu">
            <p className="kolin-menu-day">
              {currentLunchMenu.day}
              {currentLunchMenu.serviceWindow && <span>{currentLunchMenu.serviceWindow}</span>}
            </p>
            <ul className="kolin-menu-list">
              {[
                currentLunchMenu.soup,
                currentLunchMenu.main,
                currentLunchMenu.side,
                currentLunchMenu.extra
              ]
                .filter(Boolean)
                .map((dish, index) => (
                  <li key={index}>
                    <i className="bx bxs-circle" aria-hidden="true"></i>
                    {dish}
                  </li>
                ))}
            </ul>
          </div>
        ) : (
          <p className="kolin-empty">Menü bilgisi yakında paylaşılacak.</p>
        )}
      </section>
    </div>
  );
};

export default DashboardPage;
